import RidingResult from './RidingResult'
import AbstractResult from './AbstractResult'
import PartyResultMap from './PartyResultMap'
import PartyResult from './PartyResult'

export default class MultiSeatResult extends AbstractResult {
  protected _ridingResults: RidingResult[]

  public constructor(ridingResults: RidingResult[]) {
    super()
    this._ridingResults = ridingResults
    this._totalVoteCount = 0
    this._totalSeatCount = 0
    this.calculateTotalVotesAndSeats()
    this.calculatePartyResults()
  }

  protected calculateTotalVotesAndSeats(): void {
    this._ridingResults.forEach((ridingResult): void => {
      this._totalVoteCount += ridingResult.totalVoteCount
      this._totalSeatCount++
    })
  }

  protected calculatePartyResults(): void {
    const partyResults = new PartyResultMap()

    this._ridingResults.forEach((ridingResult: RidingResult): void => {
      for (const ridingPartyResult of Object.values(
        ridingResult.partyResultMap.map()
      )) {
        const party = ridingPartyResult.party
        const partyResult =
          partyResults.resultForParty(party) || new PartyResult(party)
        partyResult.voteCount += ridingPartyResult.voteCount
        partyResult.seatCount += ridingPartyResult.seatCount
        partyResult.votePercent = partyResult.voteCount / this._totalVoteCount
        partyResult.seatPercent = partyResult.seatCount / this._totalSeatCount
        partyResults.setResult(partyResult)
      }
    })

    this._partyResultMap = partyResults

    this.determineOutcomes(partyResults)
  }

  protected determineOutcomes(partyResults: PartyResultMap): void {
    const majorityThreshold = Math.ceil(this._totalSeatCount / 2)

    const sorted = this._partyResultMap.resultsBySeatCount()

    if (sorted.length === 0) {
      return
    }

    let index = 0
    let oppositionAssigned = false

    // Identify ties
    while (sorted[0].seatCount === sorted[index + 1].seatCount) {
      // There is a tie
      const firstTie = partyResults.resultForParty(sorted[0].party)
      const secondTie = partyResults.resultForParty(sorted[index + 1].party)
      if (firstTie) firstTie.outcome = 'tie'
      if (secondTie) secondTie.outcome = 'tie'
      oppositionAssigned = true
      index += 2
    }

    if (index === 0) {
      // There were no ties; party at index 0 is the winner. But is it a majority?
      if (sorted[0].seatCount >= majorityThreshold) {
        const majority = partyResults.resultForParty(sorted[index].party)
        if (majority) majority.outcome = 'majority'
      } else {
        const minority = partyResults.resultForParty(sorted[index].party)
        if (minority) minority.outcome = 'minority'
      }
      index++
    }

    // Assign opposition, third party, and no-seats parties
    for (index; index < sorted.length; index++) {
      if (sorted[index].seatCount > 0) {
        if (!oppositionAssigned) {
          const opposition = partyResults.resultForParty(sorted[index].party)
          if (opposition) opposition.outcome = 'opposition'
          oppositionAssigned = true
        } else {
          const thirdParty = partyResults.resultForParty(sorted[index].party)
          if (thirdParty) thirdParty.outcome = 'third-party'
        }
      } else {
        const noSeats = partyResults.resultForParty(sorted[index].party)
        if (noSeats) noSeats.outcome = 'no-seats'
      }
    }
  }

  public ridingResults(
    filter?: (ridingResult: RidingResult) => boolean
  ): RidingResult[] {
    return filter ? this._ridingResults.filter(filter) : this._ridingResults
  }

  public resultForRiding(ridingId: number): RidingResult {
    const result = this._ridingResults.find(
      (ridingResult): boolean => ridingResult.riding.key === `${ridingId}`
    )
    if (!result) {
      throw new Error(`resultForRiding: No riding found with id ${ridingId}`)
    }
    return result
  }
}
